import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { NewTicketComponent } from 'src/app/components/new-lok/new-ticket/new-ticket.component';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { PlacesService } from 'src/app/services/places.service';
import { SeatMapService } from 'src/app/services/seat-map.service';
import { TicketsService } from 'src/app/services/tickets.service';

@Component({
  selector: 'app-tickets-venue',
  templateUrl: './tickets-venue.component.html',
  styleUrls: ['./tickets-venue.component.scss']
})
export class TicketsVenueComponent implements OnInit {

  idVenue!: number;
  venue!: any;
  tickets: any[] = [];
  levels: any[] = [];

  hasMap: boolean = false;
  loaded: boolean = false;
  allowCreate: boolean = false;

  expandedTickets: Set<number> = new Set();

  private nameBeforeEdit: { [id: number]: string } = {};
  private priceBeforeEdit: { [id: number]: any } = {};
  private levelPriceBeforeEdit: { [key: string]: any } = {};

  currentNameEdit: { [id: number]: string } = {};
  currentPriceEdit: { [id: number]: string } = {};
  currentLevelEdit: { [key: string]: string } = {};

  savedFields: Set<string> = new Set();
  dirtyFields: Set<string> = new Set();
  toastMsg: string = '';
  toastVisible: boolean = false;
  private toastTimer: any = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private placeService: PlacesService,
    private ticketService: TicketsService,
    private seatMapService: SeatMapService,
    private dialog: MatDialog,
    private auth: AuthenticationService
  ) { }

  ngOnInit(): void {
    const roles = this.auth.getProfile()?.roles.map((r: any) => r.name);
    this.allowCreate = roles?.includes('superadmin');
  }

  ngAfterViewInit(): void {
    this.activatedRoute.params.subscribe({
      next: (params) => {
        this.idVenue = +params['idVenue'];
        this.loadVenue();
      }
    });
  }

  loadVenue(): void {
    this.placeService.getPlaceById(this.idVenue).subscribe({
      next: (res: any) => {
        this.venue = res.data;
        this.hasMap = !!this.venue?.current_version;
        if (this.hasMap) {
          this.seatMapService.getLevelsVersionMap(this.idVenue, this.venue.current_version).subscribe({
            next: (res: any) => {
              this.levels = res.data || [];
              this.levels.sort((a: any, b: any) => {
                const oa = a?.orderBuy ?? a?.order_buy ?? Infinity;
                const ob = b?.orderBuy ?? b?.order_buy ?? Infinity;
                return oa - ob;
              });
            }
          });
        }
        this.loadTickets();
      }
    });
  }

  loadTickets(): void {
    this.loaded = false;
    this.ticketService.getTicketsBySpace(this.idVenue).subscribe({
      next: (res: any) => {
        this.tickets = res.data || [];
        this.loaded = true;
      }
    });
  }

  toggleExpand(ticketId: number): void {
    if (this.expandedTickets.has(ticketId)) {
      this.expandedTickets.delete(ticketId);
    } else {
      this.expandedTickets.add(ticketId);
    }
  }

  isExpanded(ticketId: number): boolean {
    return this.expandedTickets.has(ticketId);
  }

  getTicketLevel(ticket: any, levelId: number) {
    if (!ticket.active) {
      return { price: null, active: false };
    }
    if (ticket.ticket_seats && ticket.ticket_seats.length > 0) {
      const seatLevel = ticket.ticket_seats.find((s: any) => s.seat.id_level == levelId);
      return seatLevel || { price: null, active: false, hidden: true };
    }
    const levels = ticket.ticket_levels || ticket.levels;
    const level = levels?.find((l: any) => l.level_id == levelId || l.id == levelId);
    return level || { price: null, active: false };
  }

  // ── Nombre inline ──────────────────────────────────────

  onNameFocus(ticket: any): void {
    this.nameBeforeEdit[ticket.id] = ticket.name;
  }

  saveName(ticket: any, index: number, event: any): void {
    const newName = event.target.value?.trim();
    if (!newName || newName === this.nameBeforeEdit[ticket.id]) {
      this.dirtyFields.delete(`name_${ticket.id}`);
      return;
    }
    this.ticketService.editTicket(ticket.id, { name: newName }).subscribe({
      next: (res) => {
        this.tickets[index] = res.data;
        this.markSaved(`name_${ticket.id}`);
      },
      error: () => {
        event.target.value = this.nameBeforeEdit[ticket.id];
        this.dirtyFields.delete(`name_${ticket.id}`);
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  // ── Precio inline (sin mapa) ───────────────────────────

  onPriceFocus(ticket: any): void {
    this.priceBeforeEdit[ticket.id] = ticket.price;
  }

  savePrice(ticket: any, index: number, event: any): void {
    const newPrice = parseFloat(event.target.value);
    if (isNaN(newPrice) || newPrice === parseFloat(this.priceBeforeEdit[ticket.id])) {
      this.dirtyFields.delete(`price_${ticket.id}`);
      return;
    }
    this.ticketService.editTicket(ticket.id, { price: newPrice }).subscribe({
      next: (res) => {
        this.tickets[index] = res.data;
        this.markSaved(`price_${ticket.id}`);
      },
      error: () => {
        event.target.value = this.priceBeforeEdit[ticket.id];
        this.dirtyFields.delete(`price_${ticket.id}`);
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  // ── Precio por zona (con mapa) ─────────────────────────

  onLevelPriceFocus(ticket: any, levelId: number, event: any): void {
    this.levelPriceBeforeEdit[`${ticket.id}_${levelId}`] = event.target.value;
  }

  saveLevelPrice(ticket: any, levelId: number, ticketIndex: number, event: any): void {
    const key = `${ticket.id}_${levelId}`;
    const newVal = event.target.value;
    if (newVal === this.levelPriceBeforeEdit[key]) {
      this.dirtyFields.delete(`level_${key}`);
      return;
    }
    const newPrice = newVal === '' ? null : parseFloat(newVal);
    this.ticketService.updateTicketLevelPrice(ticket.id, levelId, newPrice).subscribe({
      next: (res) => {
        this.tickets[ticketIndex] = res.data;
        this.markSaved(`level_${key}`);
      },
      error: () => {
        event.target.value = this.levelPriceBeforeEdit[key];
        this.dirtyFields.delete(`level_${key}`);
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  saveRowFields(ticket: any, index: number): void {
    if (this.isDirty(`name_${ticket.id}`)) {
      const newName = this.currentNameEdit[ticket.id]?.trim();
      if (newName && newName !== this.nameBeforeEdit[ticket.id]) {
        this.ticketService.editTicket(ticket.id, { name: newName }).subscribe({
          next: (res) => { this.tickets[index] = res.data; this.markSaved(`name_${ticket.id}`); },
          error: () => {
            this.dirtyFields.delete(`name_${ticket.id}`);
            this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
          }
        });
      } else {
        this.dirtyFields.delete(`name_${ticket.id}`);
      }
    }
    if (this.isDirty(`price_${ticket.id}`)) {
      const newPrice = parseFloat(this.currentPriceEdit[ticket.id]);
      if (!isNaN(newPrice) && newPrice !== parseFloat(this.priceBeforeEdit[ticket.id])) {
        this.ticketService.editTicket(ticket.id, { price: newPrice }).subscribe({
          next: (res) => { this.tickets[index] = res.data; this.markSaved(`price_${ticket.id}`); },
          error: () => {
            this.dirtyFields.delete(`price_${ticket.id}`);
            this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
          }
        });
      } else {
        this.dirtyFields.delete(`price_${ticket.id}`);
      }
    }
  }

  saveLevelFromButton(ticket: any, levelId: number, ticketIndex: number): void {
    const key = `${ticket.id}_${levelId}`;
    const raw = this.currentLevelEdit[key];
    const newPrice = raw === '' || raw === undefined ? null : parseFloat(raw);
    this.ticketService.updateTicketLevelPrice(ticket.id, levelId, newPrice).subscribe({
      next: (res) => { this.tickets[ticketIndex] = res.data; this.markSaved(`level_${key}`); },
      error: () => {
        this.dirtyFields.delete(`level_${key}`);
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  // ── Toggles ────────────────────────────────────────────

  toggleTicketActive(ticket: any, index: number, event: any): void {
    const newState = event.checked;
    this.ticketService.toggleTicket(ticket.id, newState).subscribe({
      next: () => {
        this.tickets[index].active = newState;
        this.showToast(newState ? 'Entrada activada' : 'Entrada desactivada');
      },
      error: () => {
        event.source.checked = !newState;
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  toggleAllowDiscounts(ticket: any, index: number, event: any): void {
    const newState = event.checked;
    this.ticketService.editTicket(ticket.id, { allow_discounts: newState }).subscribe({
      next: () => {
        this.tickets[index].allow_discounts = newState;
        this.showToast(newState ? 'Descuentos activados' : 'Descuentos desactivados');
      },
      error: () => {
        event.source.checked = !newState;
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  toggleOnlyTpvSales(ticket: any, index: number, event: any): void {
    const newState = event.checked;
    this.ticketService.editTicket(ticket.id, { only_tpv_sales: newState }).subscribe({
      next: () => {
        this.tickets[index].only_tpv_sales = newState;
        this.showToast(newState ? 'Solo taquilla activado' : 'Solo taquilla desactivado');
      },
      error: () => {
        event.source.checked = !newState;
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  toggleTicketLevelActive(ticket: any, levelId: number, event: any): void {
    const ticketLevel = this.getTicketLevel(ticket, levelId);
    if (ticketLevel.price == null) {
      event.source.checked = false;
      this.infoDialog('Esta zona no tiene precio configurado. Configura el precio primero para poder activarla.', true);
      return;
    }
    const newState = event.checked;
    this.ticketService.toggleTicketLevel(ticket.id, levelId, newState).subscribe({
      next: () => {
        this.loadTickets();
        this.showToast(newState ? 'Zona activada' : 'Zona desactivada');
      },
      error: () => {
        event.source.checked = !newState;
        this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true);
      }
    });
  }

  // ── Feedback helpers ───────────────────────────────────

  markDirty(key: string): void {
    this.dirtyFields.add(key);
  }

  isDirty(key: string): boolean {
    return this.dirtyFields.has(key);
  }

  markSaved(key: string): void {
    this.dirtyFields.delete(key);
    this.savedFields.add(key);
    setTimeout(() => this.savedFields.delete(key), 1800);
  }

  isSaved(key: string): boolean {
    return this.savedFields.has(key);
  }

  showToast(msg: string): void {
    this.toastMsg = msg;
    this.toastVisible = true;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { this.toastVisible = false; }, 2200);
  }

  // ── Crear (sigue usando el dialog) ────────────────────

  openCreateTicket(): void {
    const dialogRef = this.dialog.open(NewTicketComponent, {
      panelClass: ['info-dialog', 'not-padding'],
      data: {
        ticket: null,
        venue: this.venue,
        seats: [],
        levels: this.levels,
        idEvent: null,
        eventName: this.venue?.name,
        useMapVenue: this.hasMap,
        // Butacas especiales ya definidas en el recinto, para poder crear otra
        // entrada sobre ellas (p. ej. la invitación de las sillas).
        specialSeatsPool: this.ticketService.specialSeatsPool(this.tickets),
        specialSeatGroups: this.ticketService.specialSeatGroups(this.tickets)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      // El diálogo devuelve las butacas con la forma de `ticket_seats`
      // ({seat, price}); la API del espacio las espera como {id, price}.
      const payload = {
        ...result,
        seats: (result.seats || []).map((ticketSeat: any) => ({
          id: ticketSeat.seat?.id ?? ticketSeat.id,
          price: ticketSeat.price
        }))
      };
      this.ticketService.createTicketForSpace(this.idVenue, payload).subscribe({
        next: (res) => {
          this.tickets.push(res.data);
          this.infoDialog('Entrada creada correctamente.', false);
        },
        error: () => this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true)
      });
    });
  }

  // ── Eliminar ───────────────────────────────────────────

  deleteTicket(ticket: any, index: number): void {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: true,
        text: '¿Estás seguro de que quieres eliminar esta entrada base?',
        btnCancel: true,
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ticketService.deleteTicket(ticket.id).subscribe({
          next: () => {
            this.tickets.splice(index, 1);
            this.expandedTickets.delete(ticket.id);
            this.infoDialog('Entrada eliminada correctamente.', false);
          },
          error: () => this.infoDialog('¡Vaya! Algo ha fallado, vuelve a intentarlo más tarde.', true)
        });
      }
    });
  }

  preventNegative(event: KeyboardEvent): void {
    if (['-', '+', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
    if (event.key === 'ArrowDown') {
      const input = event.target as HTMLInputElement;
      if (input.value === '' || Number(input.value) <= 0) {
        event.preventDefault();
      }
    }
  }

  infoDialog(msg: string, icon: boolean): void {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { icon, text: msg, btnCancel: false }
    });
  }
}
